import { Inject } from '@odinjs/odin';
import { ExamCardAPI } from '@philips-emr/health-professional-components/src/components';
import { AutocompleteAPI, DatePickerAPI, DeckAPI, DeckValue, FetchAPI, FilterAPI, MediaContainerAPI, MediaContainerData, ModalAPI, PanelAPI, PDFViewerAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeFetch, OnBeforeSubmit, OnCancel, OnChange, OnClear, OnClick, OnClose, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { LabExamsListAliases } from '../../../codegen';

import { Exam } from '../../../interfaces/exam';
import ReportService from '../../../services/report-service';
import { DateFilterUtils } from '../../../utils/date-filter-utils';
import BaseController from '../../BaseController';
import { BrowserService } from '../../commons/browser/browser-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ApplicationRoute } from './../../../constants/routes';
import ShowToast from '../../../utils/toast-service';
import { DeckContext } from '../../commons/Deck/deck-service';
import { ReportStatus } from './../../../constants/enums/report-status';

interface ExamGroup {
  dsGrupoExameLab: string;
  nrSeqGrupo: number;
}

interface FilterParam {
  finalDate: Date;
  initialDate: Date;
}

interface FilterParams {
  examGroupId?: number;
  examPresentationId?: number;
  examTypeId?: number;
  finalDate: Date;
  initialDate: Date;
}

@Controller({ component: 'layout-lab-exams-list', fragment: 'lab-exams-list' })
class ExamListController extends BaseController {

  patientId!: string;
  encounterId!: number;
  examGroupId!: number;
  initialDate!: string;
  finalDate!: string;
  internalNavigation!: boolean;

  @Inject('filter-exam')
    filter!: FilterAPI;

  @Inject('panel-modal-exams')
    panelModalExams!: PanelAPI;

  @Inject('deck-lab-exams')
    deckLabExamsApi!: DeckAPI;

  @Inject('modal-exams')
    modalExams!: ModalAPI;

  @Inject('exam-card')
    examCard!: ExamCardAPI;

  @Inject('pdf-viewer-exam-details')
    pdfViewer!: PDFViewerAPI;

  @Inject('panel-pdf-loader')
    panelPdfLoader!: PanelAPI

  @Inject('date-picker-initial-date')
    dpaStartDate!: DatePickerAPI;

  @Inject('date-picker-final-date')
    dpaEndDate!: DatePickerAPI;

  @Inject('autoc-exam-groups')
    autoCompleteExamGroups!: AutocompleteAPI;

  @Inject('autoc-exam-type')
    autoCompleteExamType!: AutocompleteAPI;

  @Inject('media-container-exams-list')
    mediaContainer!: MediaContainerAPI;

  @Inject('fetch-exams')
    fetchDataApi!: FetchAPI;

  @Inject(LabExamsListAliases.FETCH_EXAM_TYPE)
    fetchExamTypeDataApi!: FetchAPI;

  @OnClear({ target: 'filter-exam' })
  onFilterClear(): void {
    this.filter.clear();
    this.autoCompleteExamGroups.setValue({} as Record<string, Value>)
    this.autoCompleteExamType.setValue({} as Record<string, Value>)
  }

  updateContext(patientId: string, encounterId: number, examGroupId: number): void {
    this.patientId = patientId;
    this.encounterId = encounterId
    this.examGroupId = examGroupId;

    this.context.set({
      encounterId: encounterId,
      examGroupId: examGroupId,
      patientId: patientId,
    });

  }

  initFilter(): void {

    const patientId = this.context.get('patientId') as string;
    const encounterId = this.context.get('encounterId') as number;
    this.updateContext(patientId, encounterId, 0);

  }

  @OnMount({ target: 'layout-lab-exams-list' })
  onMount(): void {

    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const examGroupId = 0;
    this.internalNavigation = false;

    this.updateContext(patientId as string, encounterId as number, examGroupId);
    this.context.set({ browser: BrowserService.detectBrowser() });
  }

  @OnMount({ target: LabExamsListAliases.AUTOC_EXAM_PRESENTATION })
  handleMountPresentation(event: MountEvent): void {

    const fieldApi = event.target as AutocompleteAPI;

    const data = [
      {
        description: MessageService.translate('text.exam-presentation'),
        id: 1,
      },
      {
        description: MessageService.translate('text.all-patient'),
        id: 2,
      }
    ]

    fieldApi.setData(data);

    fieldApi.setValue(data[0]);
  }

  @OnBeforeFetch(LabExamsListAliases.FETCH_EXAM_TYPE)
  onFetchExamType(event: FetchEvent): void {
    if (!event) {
      return;
    }

    const fetchExamType = event.target as FetchAPI;

    if(this.examGroupId === 0){
      fetchExamType.setParameters({} as Record<string, Value>, { skip: false });
      return;
    }

    const parameters = {
      ...fetchExamType.getParameters(),
      examGroupId: this.examGroupId
    }

    fetchExamType.setParameters(parameters as Record<string, Value>, { skip: false });
  }

  @OnAfterFetch(LabExamsListAliases.FETCH_EXAMS)
  onFetchExams(event: FetchEvent): void {
    const data = event.data as Record<string, string>[];
    const deckValue = data.map(item => ({
      ...item,
      dtResultado: item.dtResultado && moment(item.dtResultado).format('L LT')
    })) as DeckValue[];
    DeckContext.setParamActivate(deckValue);
    this.deckLabExamsApi.setData(deckValue);
  }

  @OnUnmount({ target: 'layout-lab-exams-list' })
  onUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnMount({target: 'pdf-viewer-exam-details'})
  onPdfViewerMount(): void{
    this.pdfViewer.setDownloadPdfExpression('tws.view-report');
    this.pdfViewer.setOpenPdfExpression('text.open-report');
  }

  @OnMount({ target: 'filter-exam' })
  onFilterMount(event: MountEvent): void {

    let record = {
      examPresentationId: {},
      finalDate: '',
      initialDate: '',
    };

    const filter = event.target as FilterAPI;
    const restored = filter.restoreState(true);

    if ( !restored ) {
      const {
        lastMonth,
        currentDate
      } = DateFilterUtils.getDateRange();

      const presentationExams = {
        description: MessageService.translate('text.exam-presentation'),
        id: 1
      };

      record = {
        examPresentationId: presentationExams,
        finalDate: currentDate,
        initialDate: lastMonth,
      };

    } else {

      Object.assign( record, filter.getRecord() );

    }

    filter.setData(record);

    this.initialDate = record.initialDate;
    this.finalDate = record.finalDate;

    filter.submit();

  }

  @OnBeforeSubmit({ target: 'filter-exam' })
  onBeforeSubmit(event: SubmitEvent) : void {

    let params = {
      examPresentationId: event.record.examPresentationId,
      finalDate: event.record.finalDate,
      initialDate: event.record.initialDate
    } as FilterParams;

    if(event.record.examGroupId) {
      params = {...params, examGroupId: event.record.examGroupId};
    }

    if(event.record.examTypeId) {
      params = {...params, examTypeId: event.record.examTypeId};
    }

    if(DateFilterUtils.diffDate(params.finalDate, params.initialDate) > 90) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }

    this.fetchDataApi.setParameters(params as unknown as Record<string, FilterParams>);
  }

  @OnUnmount({ target: 'filter-exam' })
  onFilterUnmount(e: MountEvent): void {
    const filter = e.target as FilterAPI;

    if ( this.internalNavigation ) {

      const record = filter.getRecord() as Record<string, FilterParam>;
      filter.setData(record);

      filter.persistState();

    } else {

      filter.clearPersistedState();

    }

  }

  @OnCancel({ target: 'filter-exam' })
  onFilterCancel(): void {

    this.initFilter();
    this.filter.submit();
  }

  @OnChange({ target: 'autoc-exam-groups' })
  onExamGroupsChange(event: ChangeEvent<ExamGroup>): void {

    let examGroupId = 0;
    if(event.value) {
      const { nrSeqGrupo } = event.value as ExamGroup;

      examGroupId = nrSeqGrupo;
    }

    this.examGroupId = examGroupId;
    this.context.set({
      examGroupId: examGroupId
    });
  }

  @OnClose({target: 'modal-exams'})
  handleCloseModalExams(): void {
    this.panelModalExams.setVisible(false);
  }

  @OnClick({ target: 'exam-card' })
  onCardClick( event: ClickEvent ): void {

    this.internalNavigation = true;

    if (event.value == null) {
      return;
    }

    const { nrPrescricao, nrSeqResultado, nrSequencia, reportAvailable, type } = event.value;

    if (reportAvailable === ReportStatus.REPORT_NOT_AVAILABLE) {
      this.pdfViewer.setVisible(false);
      this.panelPdfLoader?.setVisible(false);
      this.deckLabExamsApi.setLoading(false);
      this.panelModalExams.setVisible(false);
    } else if (reportAvailable === ReportStatus.REPORT_AVAILABLE && type === 'button') {
      if(this.context.get('browser') === 'Safari') {
        this.loadReportImages({...event.value});
        return;
      }
      this.panelModalExams.setVisible(true);
      this.loadReport({...event.value});
      return;
    }

    const prescriptionId = nrPrescricao ? nrPrescricao as number: 0;
    const resultId = nrSeqResultado ? nrSeqResultado as number: 0;
    const sequenceId = nrSequencia as number;
    const baseUrl = `${ApplicationRoute.ELECTRONIC_PATIENT_CHART}${ApplicationRoute.PATIENT_LIST}${ApplicationRoute.LAB_EXAMS_LIST}`;

    if (prescriptionId) {
      applicationContext.redirect(`${baseUrl}${ApplicationRoute.LAB_EXAMS_DETAIL_PRESCRIPTION}`,{
        prescriptionId,
        sequenceId,
      });
    } else if (resultId) {
      applicationContext.redirect(`${baseUrl}${ApplicationRoute.LAB_EXAMS_DETAIL_RESULT}`, {
        resultId,
        sequenceId,
      });
    }
  }

  @OnChange({ target: 'field-exam-groups' })
  onChangeComboBoxListExam(): void {
    this.fetchExamTypeDataApi.forceFetch();
  }

  createReportData(dataInformation: Exam): Record<string, Value> {
    return {
      CD_ESTABELECIMENTO: dataInformation.cdEstabelecimento || '1',
      EXAM_NAME: dataInformation.dsExame,
      EXAM_TYPE: 'L',
      IE_FORMATO: dataInformation.ieFormatoTexto || 0,
      NR_ACESSO_DICOM: 0,
      NR_PRESCRICAO: dataInformation.nrPrescricao || '',
      NR_SEQ_RESULTADO: dataInformation.nrSeqResultado,
      NR_SEQUENCIA_P: dataInformation.nrSequencia,
    }
  }

  loadReportImages(data: Exam): void {
    const params = this.createReportData(data);
    const reportService = new ReportService(this);
    this.context.set({ reportParams: params });
    this.deckLabExamsApi.setLoading(true);

    reportService.generateReportImages(params).then(response=>{
      const imagePaths = response.body as string[];
      const reportData = imagePaths.map(reportService.getReportImage);
      return Promise.all(reportData);
    }).then(mediaContainerData => {
      this.mediaContainer.setData(mediaContainerData as unknown as MediaContainerData[]);
      this.mediaContainer.open();
    }).catch(() => {
      this.errorReport();
    }).finally(() => {
      this.deckLabExamsApi.setLoading(false);
    });
  }

  loadReport(dataInformation: Exam): void {
    const params = this.createReportData(dataInformation);

    const reportService = new ReportService(this);
    reportService.generateReport(params).then(file => {
      this.pdfViewer.setSrc(file);
      this.pdfViewer.setVisible(true);
    }).catch(() => {
      this.errorReport();
      this.pdfViewer.setVisible(false);
    }).finally(() => {
      this.panelPdfLoader?.setVisible(false);
    });
  }

  errorReport(): void {
    ToastService.open({
      dismissible: true,
      highlight: true,
      id: 'prescription-report-toast',
      level: ToastLevel.ERROR,
      message: MessageService.translate('tws.error-generate-report'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title:MessageService.translate('tws.error-generic'),
      type: ToastType.BANNER,
    });
  }
}

export default ExamListController;
